import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ArrowRight, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useMemo } from "react";
import { useLang } from "../context/LanguageContext";
import { useCart, buildWhatsAppUrl } from "../context/CartContext";
import { ChiliHeat } from "./ChiliHeat";
import { VegDot } from "./VegDot";
import { copy } from "../data/copy";
import { discountPct } from "../data/products";

const CHIP = {
    veg: { text: "VEG", cls: "chip chip-veg" },
    nonveg: { text: "N-VEG", cls: "chip chip-nonveg" },
    gongura_veg: { text: "GONGURA · V", cls: "chip chip-veg" },
    gongura_nonveg: { text: "GONGURA · NV", cls: "chip chip-nonveg" },
    sweet: { text: "SWEET", cls: "chip chip-sweet" },
};

export const ProductCard = ({ product, index }) => {
    const { lang, t } = useLang();
    const { add, items, update } = useCart();

    const nameEn = product.name.en;
    const nameTe = product.name.te;
    const defaultSize = product.sizes?.[0] || { weight: "250g", price: 0 };

    const cartItem = useMemo(
        () => items.find((i) => i.id === product.id && i.weight === defaultSize.weight),
        [items, product.id, defaultSize.weight],
    );
    const inCartQty = cartItem?.qty || 0;

    const waMessage = `Hello Kumari Pickles\n\nI'd like to order:\n• ${nameEn} (${nameTe}) — ${defaultSize.weight}\n\nPlease share delivery details & final total.`;

    const spiceLabel = product.spice ? t(copy.products.spice_labels[product.spice]) : null;
    const chip = CHIP[product.category] || CHIP.veg;
    // 250g packs carry no launch discount, so advertise the best saving across pack sizes.
    const off = Math.max(...(product.sizes || []).map((s) => discountPct(s.mrp || 0, s.price)), 0);

    const displayName =
        lang === "en" ? `${nameEn} (${nameTe})` : `${nameTe} (${nameEn})`;
    const displayNameSub =
        lang === "en" ? nameTe : nameEn;

    return (
        <motion.article
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.75, delay: (index % 3) * 0.07, ease: [0.19, 1, 0.22, 1] }}
            className="card-warm flex flex-col p-2 md:p-4"
            data-testid={`product-card-${product.id}`}
        >
            <Link
                to={`/product/${product.slug || product.id}`}
                className="round-frame aspect-[4/5] mb-3 md:mb-4"
                data-testid={`product-link-${product.id}`}
            >
                <img
                    src={product.image}
                    alt={nameEn}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                />
                <div className="absolute top-2 left-2 md:top-3 md:left-3 flex items-center gap-2">
                    <span className={chip.cls}>{chip.text}</span>
                </div>
                {off > 0 && (
                    <div
                        className="absolute bottom-2 left-2 md:bottom-3 md:left-3 bg-[color:var(--kp-terracotta)] text-[color:var(--kp-cream)] px-2 py-0.5 md:px-2.5 md:py-1 rounded-full text-[9px] md:text-[10px] font-bold tracking-mono-wide shadow-sm"
                        data-testid={`product-discount-${product.id}`}
                    >
                        {off}% {t({ en: "OFF on 1kg", te: "1kg పై తగ్గింపు" })}
                    </div>
                )}
                <div className="absolute top-2 right-2 md:top-3 md:right-3 bg-[color:var(--kp-paper)]/95 backdrop-blur-sm px-1.5 py-0.5 md:px-2 md:py-1 rounded-full text-[9px] md:text-[10px] tracking-mono-wide text-[color:var(--kp-brown)]">
                    №{String(index + 1).padStart(2, "0")}
                </div>
            </Link>

            <div className="px-1 md:px-2 flex flex-col flex-1">
                <div className="flex items-center gap-1.5 md:gap-2 mb-1 md:mb-1.5">
                    <VegDot isVeg={product.isVeg} size={10} />
                    {spiceLabel && (
                        <span
                            className="text-[9px] md:text-[10px] tracking-mono-wide"
                            style={{
                                color:
                                    product.spice === "hot"
                                        ? "var(--kp-terracotta)"
                                        : product.spice === "medium"
                                          ? "var(--kp-mustard)"
                                          : "var(--kp-banana)",
                            }}
                        >
                            {spiceLabel}
                        </span>
                    )}
                    <div className="ml-auto">
                        <ChiliHeat level={product.heat} />
                    </div>
                </div>

                <h3
                    className="font-display text-base md:text-2xl leading-tight tracking-editorial text-[color:var(--kp-ink)] font-semibold"
                    data-testid={`product-name-${product.id}`}
                >
                    {displayName}
                </h3>

                <p
                    className={`hidden md:block text-sm text-[color:var(--kp-ink-soft)] mt-2 leading-relaxed line-clamp-2 ${
                        lang === "te" ? "font-telugu-body" : "font-body"
                    }`}
                >
                    {t(product.subtitle)}
                </p>

                <div className="mt-3 md:mt-4 pt-2 md:pt-3 border-t border-[color:var(--kp-ink)]/8 flex items-end justify-between">
                    <div>
                        <div className="flex items-baseline gap-1.5">
                            <span className="font-display text-lg md:text-2xl font-bold text-[color:var(--kp-terracotta)] leading-none">
                                ₹{defaultSize.price}
                            </span>
                            {defaultSize.mrp > defaultSize.price && (
                                <span className="text-[10px] md:text-xs text-[color:var(--kp-brown)]/55 line-through" data-testid={`product-mrp-${product.id}`}>
                                    ₹{defaultSize.mrp}
                                </span>
                            )}
                        </div>
                        <div className="text-[9px] md:text-[10px] tracking-mono-wide text-[color:var(--kp-brown)]/60 mt-1">
                            {t({ en: "starts / " + defaultSize.weight, te: defaultSize.weight + " నుండి" })}
                        </div>
                    </div>
                    {/* Desktop-only inline stepper (mobile version below) */}
                    <div className="hidden md:flex items-center gap-2">
                        <AnimatePresence mode="wait" initial={false}>
                            {inCartQty === 0 ? (
                                <motion.button
                                    key="add"
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.8, opacity: 0 }}
                                    onClick={() => add(product, defaultSize)}
                                    data-testid={`add-to-cart-${product.id}`}
                                    aria-label={`Add ${nameEn}`}
                                    className="inline-flex items-center justify-center px-3 h-10 rounded-full bg-[color:var(--kp-ink)] text-[color:var(--kp-cream)] hover:bg-black transition-colors gap-1 text-[11px] font-bold tracking-mono-wide"
                                >
                                    <Plus size={14} />
                                    {t({ en: "Add", te: "చేర్చు" })}
                                </motion.button>
                            ) : (
                                <motion.div
                                    key="stepper"
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.8, opacity: 0 }}
                                    className="inline-flex items-center bg-[color:var(--kp-banana)] text-[color:var(--kp-cream)] rounded-full h-10"
                                    data-testid={`stepper-${product.id}`}
                                >
                                    <button
                                        onClick={() => update(cartItem.key, cartItem.qty - 1)}
                                        className="w-9 h-10 flex items-center justify-center"
                                        aria-label="Decrease"
                                        data-testid={`card-decrement-${product.id}`}
                                    >
                                        <Minus size={14} />
                                    </button>
                                    <span
                                        className="min-w-[24px] text-center font-bold text-sm"
                                        data-testid={`card-qty-${product.id}`}
                                    >
                                        {inCartQty}
                                    </span>
                                    <button
                                        onClick={() => update(cartItem.key, cartItem.qty + 1)}
                                        className="w-9 h-10 flex items-center justify-center"
                                        aria-label="Increase"
                                        data-testid={`card-increment-${product.id}`}
                                    >
                                        <Plus size={14} />
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        <a
                            href={buildWhatsAppUrl(waMessage)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`whatsapp-order-${product.id}`}
                            className="inline-flex items-center gap-1 bg-[color:var(--kp-terracotta)] hover:bg-[color:var(--kp-terracotta-deep)] text-[color:var(--kp-cream)] rounded-full pl-3 pr-3.5 h-10 text-[11px] tracking-mono-wide font-bold transition-colors"
                        >
                            WA <ArrowRight size={11} />
                        </a>
                    </div>
                </div>

                {/* Mobile: full-width stacked action buttons for large touch targets */}
                <div className="md:hidden mt-3 space-y-2">
                    <AnimatePresence mode="wait" initial={false}>
                        {inCartQty === 0 ? (
                            <motion.button
                                key="add-m"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                onClick={() => add(product, defaultSize)}
                                data-testid={`add-to-cart-mobile-${product.id}`}
                                aria-label={`Add ${nameEn}`}
                                className="w-full inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-[color:var(--kp-ink)] text-[color:var(--kp-cream)] text-[12px] font-bold tracking-mono-wide"
                            >
                                <Plus size={14} />
                                {t({ en: "ADD TO BASKET", te: "బాస్కెట్‌కు" })}
                            </motion.button>
                        ) : (
                            <motion.div
                                key="stepper-m"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                className="w-full flex items-center justify-between bg-[color:var(--kp-banana)] text-[color:var(--kp-cream)] rounded-full h-11"
                                data-testid={`stepper-mobile-${product.id}`}
                            >
                                <button
                                    onClick={() => update(cartItem.key, cartItem.qty - 1)}
                                    className="w-11 h-11 flex items-center justify-center"
                                    aria-label="Decrease"
                                    data-testid={`card-decrement-mobile-${product.id}`}
                                >
                                    <Minus size={16} />
                                </button>
                                <span className="font-bold text-sm" data-testid={`card-qty-mobile-${product.id}`}>
                                    {inCartQty} {t({ en: "in basket", te: "బాస్కెట్‌లో" })}
                                </span>
                                <button
                                    onClick={() => update(cartItem.key, cartItem.qty + 1)}
                                    className="w-11 h-11 flex items-center justify-center"
                                    aria-label="Increase"
                                    data-testid={`card-increment-mobile-${product.id}`}
                                >
                                    <Plus size={16} />
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <a
                        href={buildWhatsAppUrl(waMessage)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`whatsapp-order-mobile-${product.id}`}
                        className="w-full inline-flex items-center justify-center gap-1.5 h-11 bg-[color:var(--kp-terracotta)] active:bg-[color:var(--kp-terracotta-deep)] text-[color:var(--kp-cream)] rounded-full text-[12px] tracking-mono-wide font-bold"
                    >
                        <MessageCircle size={14} />
                        {t({ en: "ORDER ON WHATSAPP", te: "వాట్సాప్‌లో ఆర్డర్" })}
                    </a>
                </div>
            </div>
        </motion.article>
    );
};
